/**
 * @file AssignLicenseToUserModal.tsx
 * @desc Modal to assign available licenses to one or more users that have no license yet
 * @copyright All rights reserved @ Inarix
 */

import { FC, useState, useMemo, useRef, useCallback } from "react";
import { useTranslation } from "react-i18next";
import {
  Alert,
  Box,
  Button,
  Chip,
  Divider,
  FormControl,
  MenuItem,
  Select,
  Stack,
  Typography,
  Paper,
  Tooltip,
  LinearProgress,
} from "@mui/material";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import { LoadingButton } from "@mui/lab";
import { useDispatch, useSelector } from "react-redux";
import { ThunkDispatch } from "@reduxjs/toolkit";
import { TFunction } from "i18next";

import { PackStatus } from "../../redux/actions/licenses/licenceTypes";
import { selectUnassignedLicenses } from "../../redux/slices/licenses/licenseList";
import {
  bindLicensesToUsers,
  AssignResult,
} from "../../redux/actions/licenses/licenseListAsyncActions";
import { LicenseAssignationModalStyles } from "./licenseStyles/LicenseAssignationModal";
import { AssignLicenseToUserModalStyles as S } from "./licenseStyles/AssignLicenseToUserModal.styles";
import { PacksTooltip, packStatusColor } from "./PacksTooltip";
import { thirtyDays } from "../../utils/times";

export interface UserToAssign {
  userId: string;
  userGroupId: number;
  username: string;
}

type AssignStatus = "idle" | "loading" | "success" | "error";

interface PairState {
  status: AssignStatus;
  progress: number;
}

interface AssignLicenseToUserModalProps {
  users: UserToAssign[];
  initData: (dispatch: ThunkDispatch<any, any, any>) => void;
  t: TFunction<"global", undefined>;
  onClose: () => void;
}

const PROGRESS_TICK_MS = 80;
const PROGRESS_MAX_BEFORE_DONE = 85;

const AssignLicenseToUserModal: FC<AssignLicenseToUserModalProps> = ({
  users: initialUsers,
  initData,
  t,
  onClose,
}) => {
  const dispatch = useDispatch<ThunkDispatch<any, any, any>>();
  const { i18n } = useTranslation();
  const availableLicenses = useSelector(selectUnassignedLicenses);

  const [activeUsers, setActiveUsers] = useState<UserToAssign[]>(initialUsers);
  const [pairStates, setPairStates] = useState<Record<string, PairState>>({});
  const [committedPairs, setCommittedPairs] = useState<
    Array<{ user: UserToAssign; license: (typeof availableLicenses)[0] | null }>
  >([]);

  // Pre-suggest the first N available licenses in order, user can change them
  const [selectedLicenseIds, setSelectedLicenseIds] = useState<
    Record<string, string | null>
  >(() =>
    Object.fromEntries(
      initialUsers.map((u, i) => [u.userId, availableLicenses[i]?.id ?? null]),
    ),
  );

  const intervalsRef = useRef<Record<string, ReturnType<typeof setInterval>>>(
    {},
  );

  const usedLicenseIds = useMemo(
    () =>
      new Set(
        Object.values(selectedLicenseIds).filter((id): id is string => !!id),
      ),
    [selectedLicenseIds],
  );

  const pairs = useMemo(
    () =>
      activeUsers.map((user) => ({
        user,
        license:
          availableLicenses.find(
            (l) => l.id === selectedLicenseIds[user.userId],
          ) ?? null,
      })),
    [activeUsers, availableLicenses, selectedLicenseIds],
  );

  // After assignment starts, freeze the display so the UI doesn't break when
  // the Redux store updates and availableLicenses drops to 0.
  const displayPairs = committedPairs.length > 0 ? committedPairs : pairs;
  const isCommitted = committedPairs.length > 0;

  const assignablePairs = displayPairs.filter((p) => p.license !== null);

  const isRunning = Object.values(pairStates).some(
    (s) => s.status === "loading",
  );
  const allDone =
    assignablePairs.length > 0 &&
    assignablePairs.every((p) => {
      const s = pairStates[p.user.userId];
      return s?.status === "success" || s?.status === "error";
    });

  const allSuccessful =
    allDone &&
    assignablePairs.every(
      (p) => pairStates[p.user.userId]?.status === "success",
    );

  const startProgressTick = useCallback((userId: string) => {
    const id = setInterval(() => {
      setPairStates((prev) => {
        const cur = prev[userId];
        if (!cur || cur.status !== "loading") {
          clearInterval(id);
          return prev;
        }
        const next = Math.min(
          cur.progress + (Math.random() * 6 + 3),
          PROGRESS_MAX_BEFORE_DONE,
        );
        return { ...prev, [userId]: { ...cur, progress: next } };
      });
    }, PROGRESS_TICK_MS);
    intervalsRef.current[userId] = id;
  }, []);

  const finishPair = useCallback(
    (userId: string, status: "success" | "error") => {
      clearInterval(intervalsRef.current[userId]);
      delete intervalsRef.current[userId];
      setPairStates((prev) => ({
        ...prev,
        [userId]: {
          status,
          progress: status === "success" ? 100 : (prev[userId]?.progress ?? 0),
        },
      }));
    },
    [],
  );

  const handleConfirm = async () => {
    if (assignablePairs.length === 0) return;
    setCommittedPairs(pairs);
    const initial: Record<string, PairState> = {};
    assignablePairs.forEach((p) => {
      initial[p.user.userId] = { status: "loading", progress: 0 };
    });
    setPairStates(initial);
    assignablePairs.forEach((p) => startProgressTick(p.user.userId));

    const results: AssignResult[] = await (
      dispatch(
        bindLicensesToUsers(
          assignablePairs.map((p) => ({
            userId: p.user.userId,
            userGroupId: p.user.userGroupId,
            licenseId: p.license!.id,
          })),
        ),
      ) as any
    ).unwrap();

    results.forEach((r) => finishPair(r.userId, r.status));
    if (results.some((r) => r.status === "success")) initData(dispatch);
  };

  const handleLicenseSelect = useCallback(
    (userId: string, licenseId: string) => {
      setSelectedLicenseIds((prev) => ({
        ...prev,
        [userId]: licenseId || null,
      }));
    },
    [],
  );

  const removeUser = (userId: string) => {
    setActiveUsers((prev) => prev.filter((u) => u.userId !== userId));
    setSelectedLicenseIds((prev) => {
      const next = { ...prev };
      delete next[userId];
      return next;
    });
  };

  return (
    <Box sx={S.container}>
      <Typography variant="h5" component="h2" gutterBottom>
        {t("licenses.assign_license_to_user")}
      </Typography>

      {/* Users chips */}
      <Typography
        variant="body2"
        color="text.secondary"
        sx={S.assigningToLabel}
      >
        {t("licenses.assigning_to")}:
      </Typography>
      <Box sx={S.chipsContainer}>
        {activeUsers.map((u) => {
          const s = pairStates[u.userId];
          const isDone = s?.status === "success" || s?.status === "error";
          return (
            <Chip
              key={u.userId}
              label={u.username}
              onDelete={
                !isRunning && !isDone && activeUsers.length > 1
                  ? () => removeUser(u.userId)
                  : undefined
              }
              color={
                s?.status === "success"
                  ? "success"
                  : s?.status === "error"
                    ? "warning"
                    : "primary"
              }
              variant="outlined"
              size="small"
            />
          );
        })}
        {activeUsers.length === 0 && (
          <Typography variant="body2" color="text.secondary">
            {t("licenses.no_users_selected")}
          </Typography>
        )}
      </Box>

      {/* Assignment pairs */}
      {activeUsers.length > 0 && (
        <Paper variant="outlined" sx={S.pairsPaper}>
          <Box sx={S.pairsScrollBox}>
            {displayPairs.map((pair, index) => {
              const isLast = index === displayPairs.length - 1;
              const state = pairStates[pair.user.userId];

              // Options for this user's Select: exclude licenses already chosen by others
              const optionsForUser = availableLicenses.filter(
                (l) =>
                  !usedLicenseIds.has(l.id) ||
                  l.id === selectedLicenseIds[pair.user.userId],
              );

              return (
                <Box key={pair.user.userId}>
                  <Box sx={S.pairRowBox(state?.status !== "idle" && !!state)}>
                    <Box sx={S.mainRow}>
                      {/* License selector */}
                      <Box sx={S.licenseInfoBox}>
                        <FormControl
                          fullWidth
                          size="small"
                          disabled={isCommitted}
                        >
                          <Select
                            value={selectedLicenseIds[pair.user.userId] ?? ""}
                            onChange={(e) =>
                              handleLicenseSelect(
                                pair.user.userId,
                                e.target.value,
                              )
                            }
                            displayEmpty
                            renderValue={(value) => {
                              if (!value)
                                return (
                                  <Typography
                                    variant="caption"
                                    sx={S.selectPlaceholder}
                                  >
                                    {t("licenses.select_license_placeholder")}
                                  </Typography>
                                );
                              // After commit availableLicenses may no longer have the license
                              const lic =
                                availableLicenses.find((l) => l.id === value) ??
                                committedPairs.find(
                                  (cp) => cp.user.userId === pair.user.userId,
                                )?.license;
                              if (!lic)
                                return (
                                  <Typography
                                    variant="caption"
                                    fontFamily="monospace"
                                  >
                                    {(value as string).slice(0, 14)}…
                                  </Typography>
                                );
                              const licExpiry = new Date(lic.expiryDate);
                              const licIsExpiringSoon =
                                licExpiry.getTime() - Date.now() < thirtyDays;
                              return (
                                <Box>
                                  <Typography
                                    variant="caption"
                                    fontFamily="monospace"
                                    sx={S.selectValueIdLine}
                                  >
                                    {t("licenses.licence_id")}:{" "}
                                    {lic.id.slice(0, 14)}…
                                  </Typography>
                                  <Stack
                                    direction="row"
                                    alignItems="center"
                                    gap={0.5}
                                  >
                                    <Typography
                                      variant="caption"
                                      color="text.secondary"
                                    >
                                      {t("licenses.expiration")}:{" "}
                                      {licExpiry.toLocaleDateString(
                                        i18n.language,
                                      )}
                                    </Typography>
                                    {licIsExpiringSoon && (
                                      <WarningAmberIcon
                                        sx={S.expiringSoonIcon}
                                      />
                                    )}
                                  </Stack>
                                </Box>
                              );
                            }}
                          >
                            {optionsForUser.length === 0 && (
                              <MenuItem disabled>
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                  fontStyle="italic"
                                >
                                  {t("licenses.noLicense")}
                                </Typography>
                              </MenuItem>
                            )}
                            {optionsForUser.map((lic) => {
                              const licExpiry = new Date(lic.expiryDate);
                              const licIsExpiringSoon =
                                licExpiry.getTime() - Date.now() < thirtyDays;
                              const licPacks = (lic as any).packs ?? [];
                              const licActivePack = licPacks.find(
                                (p: any) =>
                                  p.statusConstantId === PackStatus.active,
                              );
                              const licBoundPacks = licPacks.filter(
                                (p: any) =>
                                  p.statusConstantId === PackStatus.bound &&
                                  new Date(p.expiryDate).getTime() > Date.now(),
                              );
                              const licBoundTotal = licBoundPacks.reduce(
                                (s: number, p: any) => s + p.initialCredits,
                                0,
                              );
                              return (
                                <MenuItem
                                  disabled={
                                    new Date(lic.expiryDate).getTime() <
                                    Date.now()
                                  }
                                  key={lic.id}
                                  value={lic.id}
                                >
                                  <PacksTooltip
                                    packs={licPacks}
                                    t={t}
                                    language={i18n.language}
                                    placement="right"
                                  >
                                    <Box sx={S.menuItemBox}>
                                      <Box sx={S.menuItemHeader}>
                                        <Typography
                                          variant="caption"
                                          fontFamily="monospace"
                                          fontWeight={600}
                                        >
                                          {lic.id.slice(0, 20)}…
                                        </Typography>
                                        <Stack
                                          direction="row"
                                          alignItems="center"
                                          gap={0.5}
                                          sx={S.menuItemExpiry}
                                        >
                                          <Typography
                                            variant="caption"
                                            color="text.secondary"
                                          >
                                            {t("licenses.expiration")}:{" "}
                                            {licExpiry.toLocaleDateString(
                                              i18n.language,
                                            )}
                                          </Typography>
                                          {licIsExpiringSoon && (
                                            <WarningAmberIcon
                                              sx={S.menuItemWarningIcon}
                                            />
                                          )}
                                        </Stack>
                                      </Box>
                                      {licPacks.length > 0 && (
                                        <Box sx={S.menuItemPacksRow}>
                                          {licActivePack && (
                                            <Typography sx={S.menuItemPackText}>
                                              <Box
                                                component="span"
                                                sx={S.menuItemPackDot(
                                                  packStatusColor(
                                                    PackStatus.active,
                                                  ),
                                                )}
                                              >
                                                ●
                                              </Box>
                                              Active:{" "}
                                              {(
                                                lic as any
                                              ).availableCredits?.toLocaleString()}{" "}
                                              cr.
                                            </Typography>
                                          )}
                                          {licBoundPacks.length > 0 && (
                                            <Typography sx={S.menuItemPackText}>
                                              <Box
                                                component="span"
                                                sx={S.menuItemPackDot(
                                                  packStatusColor(
                                                    PackStatus.bound,
                                                  ),
                                                )}
                                              >
                                                ●
                                              </Box>
                                              Bound: {licBoundPacks.length}× (
                                              {licBoundTotal.toLocaleString()}{" "}
                                              cr.)
                                            </Typography>
                                          )}
                                        </Box>
                                      )}
                                    </Box>
                                  </PacksTooltip>
                                </MenuItem>
                              );
                            })}
                          </Select>
                        </FormControl>

                        {/* Pack summary below select when a license is chosen */}
                        {pair.license &&
                          (() => {
                            const packs = (pair.license as any).packs ?? [];
                            if (!packs.length) return null;
                            const activePack = packs.find(
                              (p: any) =>
                                p.statusConstantId === PackStatus.active,
                            );
                            const boundPacks = packs.filter(
                              (p: any) =>
                                p.statusConstantId === PackStatus.bound &&
                                new Date(p.expiryDate).getTime() > Date.now(),
                            );
                            const boundTotal = boundPacks.reduce(
                              (s: number, p: any) => s + p.initialCredits,
                              0,
                            );
                            return (
                              <PacksTooltip
                                packs={packs}
                                t={t}
                                language={i18n.language}
                                placement="bottom-start"
                              >
                                <Box sx={S.packSummaryBox}>
                                  {activePack && (
                                    <Typography sx={S.packSummaryText}>
                                      <Box
                                        component="span"
                                        sx={S.packSummaryDot(
                                          packStatusColor(PackStatus.active),
                                        )}
                                      >
                                        ●
                                      </Box>
                                      Active:{" "}
                                      {(
                                        pair.license as any
                                      ).availableCredits?.toLocaleString()}{" "}
                                      cr.
                                    </Typography>
                                  )}
                                  {boundPacks.length > 0 && (
                                    <Typography sx={S.packSummaryText}>
                                      <Box
                                        component="span"
                                        sx={S.packSummaryDot(
                                          packStatusColor(PackStatus.bound),
                                        )}
                                      >
                                        ●
                                      </Box>
                                      Bound: {boundPacks.length}× (
                                      {boundTotal.toLocaleString()} cr.)
                                    </Typography>
                                  )}
                                </Box>
                              </PacksTooltip>
                            );
                          })()}
                      </Box>

                      {/* Arrow — green on success */}
                      <ArrowForwardIcon
                        sx={S.arrowIcon(state?.status === "success")}
                        fontSize="small"
                      />

                      {/* User + status icon */}
                      <Box sx={S.userStatusBox}>
                        <Typography variant="body2" fontWeight={500} noWrap>
                          {pair.user.username}
                        </Typography>
                        {state?.status === "success" && (
                          <CheckCircleIcon sx={S.successIcon} />
                        )}
                        {state?.status === "error" && (
                          <Tooltip title={t("licenses.assign_error")}>
                            <CancelIcon sx={S.errorIcon} />
                          </Tooltip>
                        )}
                      </Box>
                    </Box>

                    {/* Progress bar */}
                    {(state?.status === "loading" ||
                      state?.status === "success" ||
                      state?.status === "error") && (
                      <Box sx={S.progressBarBox}>
                        <LinearProgress
                          variant="determinate"
                          value={state.progress}
                          sx={S.progressBar(state.status, PROGRESS_TICK_MS)}
                        />
                      </Box>
                    )}
                  </Box>
                  {!isLast && <Divider />}
                </Box>
              );
            })}
          </Box>
        </Paper>
      )}

      {allDone && (
        <Alert
          severity={allSuccessful ? "success" : "warning"}
          sx={S.doneAlert}
        >
          {allSuccessful
            ? t("licenses.all_assigned_success")
            : t("licenses.partial_assigned")}
        </Alert>
      )}

      <Stack direction="row" spacing={2}>
        <LoadingButton
          loading={isRunning}
          variant="contained"
          color="primary"
          fullWidth
          disabled={assignablePairs.length === 0 || allDone}
          sx={LicenseAssignationModalStyles.button}
          onClick={handleConfirm}
        >
          {t("Licenses.assign_button")}
          {assignablePairs.length > 1 ? ` (${assignablePairs.length})` : ""}
        </LoadingButton>
        <Button
          disabled={isRunning}
          variant="outlined"
          color="inherit"
          fullWidth
          sx={LicenseAssignationModalStyles.button}
          onClick={onClose}
        >
          {allDone ? t("Common_options.Close") : t("Common_options.Cancel")}
        </Button>
      </Stack>
    </Box>
  );
};

export default AssignLicenseToUserModal;
